import { useEvents } from '@api/useEvents'
import { defaultDateDef } from '@components/_SiteWide/CellTemplates/typeColumnDefs'
import { eventCreateConfig } from '@components/AnalyticsReporting/Event/eventCreateConfig'
import { DatePickerEditor, GraviButton, GraviGrid, Vertical } from '@gravitate-js/excalibrr'
import { useUserSettings } from '@hooks/settings'
import { formatters } from '@utils/intl/dateTimeFormatters'
import moment from 'moment'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { DateRangeFilter } from '@/components/_SiteWide/DateRangeFilter'
import { useLocalStorage } from '@gravitate-js/the-armory'
import { Dropdown } from 'antd'
import { gridViewManager } from '@/utils/grid'

const STORAGE_KEY = 'events'

export function Event() {
  const { createEventMutation, eventsQuery } = useEvents()
  const { value: dayRange, setValue: setLocalDayRange } = useLocalStorage('analytics-reporting-sales-dates', {
    back: -90,
    ahead: 90,
  })
  const [filters, setFilters] = useState({
    To: moment().add(90, 'days').toDate(),
    From: moment().add(-90, 'days').toDate(),
  })
  const { data: eventsOverviewData, status } = eventsQuery({
    from_date: moment(filters.From).format('YYYY-MM-DDT00:00:00'),
    to_date: moment(filters.To).format('YYYY-MM-DDT00:00:00'),
  })
  const { updateSetting } = useUserSettings()
  const gridAPIRef = useRef(null)

  const columnDefs = getColumnDefs()
  const getRowId = useCallback((row) => row.data?.id, [])
  const agPropOverrides = useMemo(() => ({ getRowId }), [getRowId])

  const setDayRange = async (range) => {
    setLocalDayRange(range)
    await updateSetting('analytics-reporting-sales-dates', range)
  }

  return (
    <>
      <GraviGrid
        storageKey={STORAGE_KEY}
        rowData={eventsOverviewData}
        externalRef={gridAPIRef}
        loading={status === 'pending'}
        createEP={createEventMutation.mutate}
        createConfig={eventCreateConfig}
        agPropOverrides={agPropOverrides}
        columnDefs={columnDefs}
        controlBarProps={{
          title: 'Event Management',
          actionButtons: (
            <ActionButtons
              gridAPIRef={gridAPIRef}
              filters={filters}
              setFilters={setFilters}
              setDayRange={setDayRange}
              dayRangeStorage={dayRange}
            />
          ),
        }}
        onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
        {...gridViewManager(STORAGE_KEY, updateSetting)}
      />
    </>
  )
}

export const ActionButtons = ({ gridAPIRef, filters, setFilters, setDayRange, dayRangeStorage }) => {
  return (
    <>
      <DateRangeFilter
        currentFilters={filters}
        setCurrentFilters={setFilters}
        getFromStorage
        dayRangeStorage={dayRangeStorage}
        setDayRangeStorage={setDayRange}
        min={-90}
        max={90}
      />
    </>
  )
}

//
const getColumnDefs = () => [
  {
    field: 'name',
    filter: 'true',
  },
  {
    headerName: 'From Date',
    field: 'from_date',
    valueGetter: (params) => moment(params.data.from_date),
    cellRenderer: ({ value }) => formatters.DATE(value),
    ...defaultDateDef('from_date'),
  },
  {
    headerName: 'To Date',
    cellEditor: DatePickerEditor,
    field: 'to_date',
    valueGetter: (params) => moment(params.data.to_date),
    cellRenderer: ({ value }) => formatters.DATE(value),
    ...defaultDateDef('to_date'),
  },
  {
    field: 'type',
    cellEditor: 'agRichTextCellEditor',
    cellEditorPopup: true,
    filter: 'true',
  },
  {
    field: 'markets',
    cellEditor: 'agRichTextCellEditor',
    cellEditorPopup: true,
    filter: 'true',
  },
  {
    field: 'sectors',
    cellEditor: 'agRichTextCellEditor',
    cellEditorPopup: true,
    filter: 'true',
  },
  {
    field: 'stores',
    cellEditor: 'agRichTextCellEditor',
    cellEditorPopup: true,
    filter: 'true',
  },
  {
    field: 'products',
    cellEditor: 'agRichTextCellEditor',
    cellEditorPopup: true,
    filter: 'true',
  },
  {
    field: 'product_groups',
    cellEditor: 'agRichTextCellEditor',
    cellEditorPopup: true,
    filter: 'true',
    headerName: 'Product Group',
  },
  {
    field: 'actions',
    cellRenderer: (params) => {
      const inFuture = moment(params.data.from_date).isAfter(moment())
      const hasFullyPassed = moment(params.data.to_date).isBefore(moment())

      const items = [
        {
          key: 'sales',
          label: <a href={`/reporting/sales/?event_id=${params.data.id}`}>Go to Sales</a>,
        },
        {
          key: 'forecast',
          label: <a href={`/reporting/forecast/?event_id=${params.data.id}`}>Go to Forecast</a>,
        },
      ]

      return (
        <div>
          <Vertical flex={1}>
            {inFuture && (
              <GraviButton
                type='button'
                href={`/reporting/forecast/?event_id=${params.data.id}`}
                buttonText='Go to Forecast'
                color='primary'
                flex={1}
              />
            )}
            {!inFuture && hasFullyPassed && (
              <GraviButton
                type='button'
                href={`/reporting/sales/?event_id=${params.data.id}`}
                buttonText='Go to Sales'
                color='primary'
              />
            )}
            {!inFuture && !hasFullyPassed && (
              // <GraviButton type='button' href={`/reporting/sales/?event_id=${params.data.id}`} buttonText='Go to Test' />
              <Dropdown
                menu={{
                  items,
                }}
                placement='bottomRight'
              >
                <GraviButton buttonText='See Options' color='primary' />
              </Dropdown>
            )}
          </Vertical>
        </div>
      )
    },
  },
]
